import React, { useEffect, useState } from 'react';
import { ChevronDown, ChevronUp, FileText, Users, Clock, Target } from 'lucide-react';
import { projects } from '../data/mock';
import ProjectFlowchart from './ProjectFlowchart';

const Projects = () => {
  const [expandedProjects, setExpandedProjects] = useState(new Set());

  const toggleProject = (projectId) => {
    setExpandedProjects((prev) => {
      const next = new Set(prev);
      if (next.has(projectId)) {
        next.delete(projectId);
      } else {
        next.add(projectId);
      }
      return next;
    });
  };

  useEffect(() => {
    const handler = (e) => {
      const query = String(e?.detail?.query || '').trim();
      if (!query) return;
      const normalize = (s) =>
        s.toLowerCase().replace(/[^a-z0-9\s]/g, ' ').replace(/\s+/g, ' ').trim();
      const q = normalize(query);
      const qTokens = q.split(' ').filter(Boolean);
      const editDistance = (a, b) => {
        if (a === b) return 0;
        const dp = Array.from({ length: a.length + 1 }, () => Array(b.length + 1).fill(0));
        for (let i = 0; i <= a.length; i++) dp[i][0] = i;
        for (let j = 0; j <= b.length; j++) dp[0][j] = j;
        for (let i = 1; i <= a.length; i++) {
          for (let j = 1; j <= b.length; j++) {
            const cost = a[i - 1] === b[j - 1] ? 0 : 1;
            dp[i][j] = Math.min(
              dp[i - 1][j] + 1,
              dp[i][j - 1] + 1,
              dp[i - 1][j - 1] + cost
            );
          }
        }
        return dp[a.length][b.length];
      };
      const fuzzyMatch = (text) => {
        if (!q) return false;
        const t = normalize(text);
        if (t.includes(q)) return true;
        const tokens = t.split(' ').filter(Boolean);
        return qTokens.some((qt) =>
          tokens.some((tok) => {
            const maxDist = qt.length >= 7 ? 2 : 1;
            return editDistance(qt, tok) <= maxDist;
          })
        );
      };
      const matches = projects.filter((project) => {
        const haystack = [
          project.title,
          project.problem,
          project.solution,
          project.results,
          ...(project.technologies || [])
        ].join(' ');
        return fuzzyMatch(haystack);
      });
      const matchIds = matches.map((p) => p.id);
      setExpandedProjects(new Set(matchIds));
      if (matchIds.length > 0) {
        setTimeout(() => {
          const content = document.getElementById(`project-${matchIds[0]}-content`);
          if (content) {
            content.scrollIntoView({ behavior: 'smooth', block: 'start' });
          } else {
            const first = document.getElementById(`project-${matchIds[0]}`);
            if (first) first.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 150);
      }
    };
    window.addEventListener('project-search', handler);
    return () => window.removeEventListener('project-search', handler);
  }, []);

  return (
    <section id="projects" className="py-24 bg-gradient-to-b from-white to-blue-50">
      <div className="max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <div className="mb-16 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-4">
            <span className="text-blue-500">Featured</span>{' '}
            <span className="text-green-500">Projects</span>
          </h2>
          {/* <p className="text-gray-600 text-lg max-w-2xl mx-auto">
            Interactive DevOps projects with detailed problem-solution workflows
          </p> */}
          <div className="w-20 h-1 bg-gradient-to-r from-blue-500 to-green-500 mt-4 mx-auto rounded-full"></div>
        </div>

        {/* Projects Grid */}
        <div className="space-y-8">
          {projects.map((project, idx) => (
            <div
              key={project.id}
              id={`project-${project.id}`}
              className="bg-white border-2 border-gray-200 rounded-2xl overflow-hidden hover:border-blue-300 transition-all duration-300 shadow-lg hover:shadow-2xl"
            >
              {/* Project Header - Always Visible */}
              <div
                onClick={() => toggleProject(project.id)}
                className="p-8 cursor-pointer hover:bg-blue-50 transition-all duration-300"
              >
                <div className="flex items-center justify-between">
                  {/* Project Number & Title */}
                  <div className="flex items-center gap-4">
                    <span className="text-5xl font-bold text-blue-500 opacity-30">
                      {String(idx + 1).padStart(2, '0')}
                    </span>
                    <h3 className="text-2xl md:text-3xl font-bold text-gray-800">
                      {project.title}
                    </h3>
                  </div>

                  {/* Expand/Collapse Button */}
                  <button
                    className="ml-4 p-3 hover:bg-blue-100 rounded-xl transition-colors flex-shrink-0"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleProject(project.id);
                    }}
                  >
                    {expandedProjects.has(project.id) ? (
                      <ChevronUp className="text-blue-500" size={28} />
                    ) : (
                      <ChevronDown className="text-gray-400" size={28} />
                    )}
                  </button>
                </div>
              </div>

              {/* Expanded Content */}
              {expandedProjects.has(project.id) && (
                <div
                  id={`project-${project.id}-content`}
                  className="px-8 pb-8 animate-in fade-in slide-in-from-top-4 duration-500 border-t-2 border-gray-200 scroll-mt-24"
                >
                  {/* Meta Information */}
                  <div className="flex flex-wrap gap-6 mb-6 pt-6">
                    <div className="flex items-center gap-2 text-gray-600">
                      <Clock className="text-yellow-500" size={18} />
                      <span className="text-sm font-medium">{project.timeline}</span>
                    </div>
                    <div className="flex items-center gap-2 text-gray-600">
                      <Target className="text-red-500" size={18} />
                      <span className="text-sm font-medium">{project.role}</span>
                    </div>
                    <div className="flex items-center gap-2 text-gray-600">
                      <Users className="text-green-500" size={18} />
                      <span className="text-sm font-medium">Team: {project.teamSize}</span>
                    </div>
                  </div>

                  {/* Problem Statement */}
                  <div className="mb-6">
                    <h4 className="text-lg font-bold text-red-600 mb-2 flex items-center gap-2">
                      <FileText size={18} />
                      Problem
                    </h4>
                    <p className="text-gray-700 leading-relaxed bg-red-50 p-4 rounded-lg border-l-4 border-red-500">
                      {project.problem}
                    </p>
                  </div>

                  {/* Solution */}
                  <div className="mb-6">
                    <h4 className="text-lg font-bold text-blue-600 mb-2 flex items-center gap-2">
                      <FileText size={18} />
                      Solution
                    </h4>
                    <p className="text-gray-700 leading-relaxed bg-blue-50 p-4 rounded-lg border-l-4 border-blue-500">
                      {project.solution}
                    </p>
                  </div>

                  {/* Results */}
                  <div className="mb-6">
                    <h4 className="text-lg font-bold text-green-600 mb-2 flex items-center gap-2">
                      <FileText size={18} />
                      Results & Impact
                    </h4>
                    <p className="text-gray-700 leading-relaxed bg-green-50 p-4 rounded-lg border-l-4 border-green-500">
                      {project.results}
                    </p>
                  </div>

                  {/* Technologies */}
                  <div className="flex flex-wrap gap-2 mb-8">
                    {project.technologies.map((tech, techIdx) => (
                      <span
                        key={techIdx}
                        className="px-4 py-2 bg-gradient-to-r from-blue-100 to-green-100 border border-blue-200 text-blue-700 text-sm rounded-full font-semibold"
                      >
                        {tech}
                      </span>
                    ))}
                  </div>

                  {/* Technical Architecture Diagram */}
                  <div className="mt-8">
                    <h4 className="text-2xl font-bold text-gray-800 mb-6 text-center">
                      Technical Architecture
                    </h4>
                    <ProjectFlowchart flowchart={project.flowchart} projectId={project.id} />
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Projects;
